/**
 * SEO Utility Functions
 * Best practices for canonical URLs and meta tags
 */

// Production base URL - update this to your production domain
const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://printster.in';

/**
 * Generate proper canonical URL
 * Removes query parameters, hash fragments, and trailing slashes
 *
 * @param {string} path - The path of the current page
 * @returns {string} - Clean canonical URL
 */
export const getCanonicalUrl = (path = '') => {
  // Remove query parameters and hash
  const cleanPath = path.split('?')[0].split('#')[0];

  // Remove trailing slash (except for root)
  const normalizedPath = cleanPath.length > 1 && cleanPath.endsWith('/')
    ? cleanPath.slice(0, -1)
    : cleanPath;

  return `${BASE_URL}${normalizedPath}`;
};

/**
 * Generate full URL for Open Graph and social sharing
 *
 * @param {string} path - The path of the current page
 * @returns {string} - Full URL
 */
export const getFullUrl = (path = '') => {
  return getCanonicalUrl(path);
};

/**
 * Truncate text for meta descriptions
 * Google displays ~155-160 characters
 *
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length (default: 155)
 * @returns {string} - Truncated text
 */
export const truncateDescription = (text, maxLength = 155) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

/**
 * Generate keywords from array or string
 *
 * @param {Array|string} keywords - Keywords array or comma-separated string
 * @returns {string} - Comma-separated keywords string
 */
export const formatKeywords = (keywords) => {
  if (Array.isArray(keywords)) {
    return keywords.join(', ');
  }
  return keywords;
};

/**
 * Get current pathname for canonical URL
 * Works both client-side and with React Router
 *
 * @returns {string} - Current pathname
 */
export const getCurrentPath = () => {
  if (typeof window !== 'undefined') {
    return window.location.pathname;
  }
  return '/';
};

/**
 * Organization Schema - Global company information
 * Add this to your root App component
 */
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  'name': 'Printster',
  'alternateName': 'Printster India',
  'description': 'India\'s premier online document printing and binding service',
  'url': BASE_URL,
  'logo': `${BASE_URL}/images/logo.png`,
  'image': `${BASE_URL}/images/logo.png`,
  'telephone': '+91-XXXXXXXXXX', // Update with actual phone
  'email': 'info@printster.in', // Update with actual email
  'address': {
    '@type': 'PostalAddress',
    'streetAddress': 'Your Street Address', // Update
    'addressLocality': 'Delhi',
    'addressRegion': 'DL',
    'postalCode': '110001', // Update
    'addressCountry': 'IN'
  },
  'geo': {
    '@type': 'GeoCoordinates',
    'latitude': '28.6139',
    'longitude': '77.2090'
  },
  'openingHoursSpecification': [
    {
      '@type': 'OpeningHoursSpecification',
      'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      'opens': '09:00',
      'closes': '18:00'
    }
  ],
  'priceRange': '₹₹',
  'aggregateRating': {
    '@type': 'AggregateRating',
    'ratingValue': '4.8',
    'reviewCount': '250',
    'bestRating': '5',
    'worstRating': '1'
  },
  'sameAs': [
    'https://www.facebook.com/printster', // Update with actual social links
    'https://twitter.com/printster',
    'https://www.instagram.com/printster',
    'https://www.linkedin.com/company/printster'
  ]
};

/**
 * Website Schema - Add to root component
 */
export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  'name': 'Printster',
  'url': BASE_URL,
  'potentialAction': {
    '@type': 'SearchAction',
    'target': {
      '@type': 'EntryPoint',
      'urlTemplate': `${BASE_URL}/search?q={search_term_string}`
    },
    'query-input': 'required name=search_term_string'
  }
};

/**
 * Default meta tags for pages without specific meta
 */
export const defaultMetaTags = {
  title: 'Printster - Online Document Printing & Binding Services Delhi India',
  description: 'India\'s premier online document printing and binding service. Fast delivery, affordable prices, and high-quality printing. Upload documents, get doorstep delivery across India.',
  keywords: 'online printing, document printing Delhi, binding services, printout services India, online printing Delhi, document binding',
  ogImage: `${BASE_URL}/images/logo.png`
};
